<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import type { NotificationMessageOut } from '@/types/api'

const router = useRouter()
const notifStore = useNotificationsStore()
const open = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)

const count = computed(() => notifStore.unreadCount)
const items = computed<NotificationMessageOut[]>(() => notifStore.recentUnread)
const badge = computed(() => {
  const n = count.value
  return n > 99 ? '99+' : String(n)
})

function toggle() {
  open.value = !open.value
}

async function go(item: NotificationMessageOut) {
  open.value = false
  await notifStore.markRead(item.id)
  // link 可能是 "/orders/123" → router.resolve 路由
  const link = item.link
  if (link && link.startsWith('/')) {
    router.push(link).catch(() => {
      router.push('/orders')
    })
  } else {
    router.push('/orders')
  }
}

async function markAll() {
  await notifStore.markAllRead()
}

function onDocClick(e: MouseEvent) {
  if (!open.value) return
  const wrapper = wrapperRef.value
  if (wrapper && !wrapper.contains(e.target as Node)) {
    open.value = false
  }
}

function fmtTime(iso: string) {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
})
</script>

<template>
  <div ref="wrapperRef" class="relative">
    <button
      type="button"
      class="relative px-2 py-1 rounded hover:bg-white/10 transition"
      :aria-label="`通知 ${count} 条未读`"
      :title="`通知 ${count} 条未读`"
      @click.stop="toggle"
    >
      <span class="text-xl leading-none">🔔</span>
      <span
        v-if="count > 0"
        class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow"
      >
        {{ badge }}
      </span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 mt-1 w-[360px] bg-white text-gray-800 rounded-lg shadow-xl z-50 border border-gray-200 overflow-hidden"
    >
      <div class="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <span class="text-sm font-medium">通知（{{ count }} 未读）</span>
        <button
          v-if="count > 0"
          @click.stop="markAll"
          class="text-xs text-blue-600 hover:underline"
        >
          全部已读
        </button>
      </div>

      <div class="max-h-[480px] overflow-y-auto">
        <div
          v-if="items.length === 0"
          class="px-4 py-8 text-center text-sm text-gray-400"
        >
          没有未读通知
        </div>
        <ul v-else class="divide-y">
          <li
            v-for="n in items"
            :key="n.id"
            class="px-3 py-2.5 hover:bg-blue-50 cursor-pointer transition"
            @click="go(n)"
          >
            <div class="flex items-start gap-2">
              <span class="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1">
                  <span class="text-sm font-medium truncate">{{ n.title }}</span>
                  <span
                    v-if="n.archived_at"
                    class="text-[10px] text-orange-600 shrink-0"
                    title="已被新版消息取代，归档保留"
                  >🗄️</span>
                </div>
                <!-- 已归档行：不展开 body；其他行显示 body -->
                <div
                  v-if="n.body && !n.archived_at"
                  class="text-xs text-gray-500 line-clamp-2 mt-0.5"
                >
                  {{ n.body }}
                </div>
                <div class="text-[10px] text-gray-400 mt-0.5">
                  {{ fmtTime(n.created_at) }}
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div class="border-t bg-gray-50 px-3 py-2 text-center">
        <router-link
          to="/notifications"
          class="text-xs text-blue-600 hover:underline"
          @click="open = false"
        >
          查看全部
        </router-link>
      </div>
    </div>
  </div>
</template>
